import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Users, Globe, TrendingUp, Clock, UserCheck,
  ArrowUpRight, ArrowDownRight, Activity, Eye, Send, Target,
  BarChart3, RefreshCw, Plus, Settings, Zap, Radio
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('all');
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [recentChats, setRecentChats] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [token, period, selectedWebsite]);

  // Progressive — each tile lands as it arrives, the spinner clears on
  // the first response, slow tiles don't block the rest of the page.
  const fetchDashboardData = () => {
    const tasks = [
      axios.get(`${API}/api/live-chat/dashboard/stats?token=${token}&period=${period}${selectedWebsite !== 'all' ? `&website_id=${selectedWebsite}` : ''}`)
        .then((r) => r.data.success && setStats(r.data.stats))
        .catch((e) => console.error('[LiveChatDashboard] stats failed:', e)),
      axios.get(`${API}/api/live-chat/websites?token=${token}&status=active`)
        .then((r) => r.data.success && setWebsites(r.data.websites))
        .catch((e) => console.error('[LiveChatDashboard] websites failed:', e)),
      axios.get(`${API}/api/live-chat/visitors?token=${token}&status=active&limit=10`)
        .then((r) => r.data.success && setActiveVisitors(r.data.visitors))
        .catch((e) => console.error('[LiveChatDashboard] visitors failed:', e)),
      axios.get(`${API}/api/live-chat/chats?token=${token}&limit=10`)
        .then((r) => r.data.success && setRecentChats(r.data.chats))
        .catch((e) => console.error('[LiveChatDashboard] chats failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const StatCard = ({ title, value, icon: Icon, change, changeType, subtitle, onClick }) => (
    <Card 
      className={`p-5 bg-white border-slate-200 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${
          changeType === 'positive' ? 'bg-emerald-50' : 
          changeType === 'negative' ? 'bg-red-50' : 'bg-slate-100'
        }`}>
          <Icon size={20} className={
            changeType === 'positive' ? 'text-emerald-600' : 
            changeType === 'negative' ? 'text-red-600' : 'text-slate-600'
          } />
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          changeType === 'positive' ? 'text-emerald-600' : 
          changeType === 'negative' ? 'text-red-600' : 'text-slate-500'
        }`}>
          {changeType === 'positive' ? <ArrowUpRight size={14} /> : 
           changeType === 'negative' ? <ArrowDownRight size={14} /> : null}
          <span>{change}%</span>
          <span className="text-slate-400 ml-1">vs last period</span>
        </div>
      )}
    </Card>
  );

  const VisitorRow = ({ visitor }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${visitor.is_online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        <div>
          <p className="text-sm font-medium text-slate-900">
            {visitor.visitor_name || visitor.visitor_email || 'Anonymous Visitor'}
          </p>
          <p className="text-xs text-slate-500 truncate max-w-[200px]">{visitor.current_page}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs">
          {visitor.page_views} pages
        </Badge>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => navigate(`/marketing/live-chat/visitors/${visitor.visitor_id}`)}
        >
          <Eye size={14} />
        </Button>
      </div>
    </div>
  );

  const ChatRow = ({ chat }) => {
    const statusColors = {
      open: 'bg-blue-50 text-blue-700',
      assigned: 'bg-violet-50 text-violet-700',
      pending: 'bg-amber-50 text-amber-700',
      resolved: 'bg-emerald-50 text-emerald-700',
      closed: 'bg-slate-100 text-slate-600'
    };

    return (
      <div 
        className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 px-2 -mx-2 rounded"
        onClick={() => navigate(`/marketing/live-chat/chats/${chat.id}`)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
            {(chat.visitor_name || 'V')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">{chat.visitor_name || 'Visitor'}</p>
            <p className="text-xs text-slate-500 truncate max-w-[180px]">
              {chat.last_message?.message || 'No messages'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-xs ${statusColors[chat.status] || statusColors.open}`}>
            {chat.status}
          </Badge>
          {chat.unread_count > 0 && (
            <Badge className="bg-red-500 text-white text-xs">{chat.unread_count}</Badge>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-chat-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Live Chat Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor website visitors and manage live conversations</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="All Websites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Websites</SelectItem>
              {websites.map(w => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchDashboardData} variant="outline" size="icon">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate('/marketing/live-chat/traffic')} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
          <Radio size={16} /> Live Traffic
        </Button>
        <Button onClick={() => navigate('/marketing/live-chat/websites')} className="gap-2" variant="outline">
          <Plus size={16} /> Add Website
        </Button>
        <Button onClick={() => navigate('/marketing/live-chat/chats?filter=unassigned')} variant="outline" className="gap-2">
          <MessageSquare size={16} /> View Unassigned Chats
        </Button>
        <Button onClick={() => navigate('/marketing/live-chat/visitors')} variant="outline" className="gap-2">
          <Users size={16} /> Live Visitors
        </Button>
        <Button onClick={() => navigate('/marketing/live-chat/analytics')} variant="outline" className="gap-2">
          <BarChart3 size={16} /> Analytics
        </Button>
        <Button onClick={() => navigate('/marketing/live-chat/rules')} variant="outline" className="gap-2">
          <Zap size={16} /> Rules
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Visitors"
          value={stats?.active_visitors || 0}
          icon={Activity}
          changeType="positive"
          subtitle="Currently on site"
          onClick={() => navigate('/marketing/live-chat/visitors')}
        />
        <StatCard
          title="Total Visitors"
          value={stats?.total_visitors || 0}
          icon={Users}
          subtitle={`In ${period === 'today' ? 'last 24h' : period === 'week' ? 'last 7 days' : 'last 30 days'}`}
        />
        <StatCard
          title="Total Chats"
          value={stats?.total_chats || 0}
          icon={MessageSquare}
          onClick={() => navigate('/marketing/live-chat/chats')}
        />
        <StatCard
          title="Missed Chats"
          value={stats?.missed_chats || 0}
          icon={Clock}
          changeType={stats?.missed_chats > 0 ? 'negative' : undefined}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Leads Created"
          value={stats?.leads_created || 0}
          icon={Target}
          changeType="positive"
        />
        <StatCard
          title="Conversion Rate"
          value={`${stats?.conversion_rate || 0}%`}
          icon={TrendingUp}
          changeType={stats?.conversion_rate > 5 ? 'positive' : undefined}
        />
        <StatCard
          title="Avg Response Time"
          value={`${Math.round(stats?.avg_response_time_seconds || 0)}s`}
          icon={Zap}
          changeType={stats?.avg_response_time_seconds < 60 ? 'positive' : 'negative'}
        />
        <StatCard
          title="Websites"
          value={websites.length}
          icon={Globe}
          onClick={() => navigate('/marketing/live-chat/websites')}
        />
      </div>

      {/* Live Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Active Visitors */}
        <Card className="p-5 bg-white border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity size={18} className="text-emerald-600" />
              <h3 className="font-medium text-slate-900">Active Visitors</h3>
              <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                {activeVisitors.filter(v => v.is_online).length} online
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/live-chat/visitors')}>
              View All
            </Button>
          </div>
          <div className="space-y-1">
            {activeVisitors.length > 0 ? (
              activeVisitors.slice(0, 5).map(visitor => (
                <VisitorRow key={visitor.visitor_id} visitor={visitor} />
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No active visitors right now</p>
            )}
          </div>
        </Card>

        {/* Recent Chats */}
        <Card className="p-5 bg-white border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-blue-600" />
              <h3 className="font-medium text-slate-900">Recent Chats</h3>
              {recentChats.filter(c => c.status === 'open').length > 0 && (
                <Badge className="bg-blue-500 text-white">
                  {recentChats.filter(c => c.status === 'open').length} open
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/live-chat/chats')}>
              View All
            </Button>
          </div>
          <div className="space-y-1">
            {recentChats.length > 0 ? (
              recentChats.slice(0, 5).map(chat => (
                <ChatRow key={chat.id} chat={chat} />
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No chat conversations yet</p>
            )}
          </div>
        </Card>
      </div>

      {/* Getting Started */}
      {websites.length === 0 && (
        <Card className="p-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Globe size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-1">Get Started with Live Chat</h3>
              <p className="text-slate-300 text-sm mb-4">
                Add your website to start tracking visitors and engaging with them through live chat.
              </p>
              <Button 
                onClick={() => navigate('/marketing/live-chat/websites/new')}
                className="bg-white text-slate-900 hover:bg-slate-100"
              >
                <Plus size={16} className="mr-2" /> Add Your First Website
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default LiveChatDashboard;
